import React, { useState, useEffect } from "react";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { whitelistDomain } from "../api";

const WhiteListDomain = ({ restaurantData }) => {
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAccountIdAlert, setShowAccountIdAlert] = useState(false);
  const [domainStatuses, setDomainStatuses] = useState([]);

  // Get account ID directly from restaurant data
  const accountId = restaurantData?.paymentSettings?.stripeDestinationAccountId || 
                    restaurantData?.paymentInfo?.destinationAccountId;

  useEffect(() => {
    if (restaurantData) {
      if (accountId) {
        setShowAccountIdAlert(false);
      } else {
        setShowAccountIdAlert(true);
      }
    }
  }, [restaurantData, accountId]);

  const handleSubmit = async () => {
    if (!websiteUrl.trim() || !accountId) return;

    setLoading(true);
    setDomainStatuses([]);

    // Generate all domains to whitelist
    const domains = [
      "menu.thefoodo.com",
      "www.menu.thefoodo.com",
      websiteUrl.trim(),
      `www.${websiteUrl.trim()}`
    ];

    // Call API for all domains in parallel
    const promises = domains.map(async (domain) => {
      try {
        await whitelistDomain({ domain, accountId });
        return { domain, status: "success", message: "Whitelisted successfully" };
      } catch (err) {
        return { domain, status: "error", message: "Failed to whitelist" };
      }
    });

    const allResults = await Promise.all(promises);
    setDomainStatuses(allResults);
    setLoading(false);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, margin: "0 auto" }}>
      <Typography variant="h5" mb={2}>Whitelist Domain</Typography>
      
      {showAccountIdAlert && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please add destination account ID in payment settings first.
        </Alert>
      )}

      <TextField
        label="Website URL"
        value={websiteUrl}
        onChange={e => setWebsiteUrl(e.target.value)}
        fullWidth
        margin="normal"
        placeholder="restaurantname.com"
        helperText="Enter your restaurant's website domain (without www or http://)"
      />
      
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Stripe Account ID
        </Typography>
        <Typography variant="body1" sx={{ 
          p: 1.5, 
          border: '1px solid #ddd', 
          borderRadius: 1, 
          backgroundColor: '#f5f5f5',
          fontFamily: 'monospace'
        }}>
          {accountId || 'No account ID found'}
        </Typography>
      </Box>

      {websiteUrl.trim() && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Domains to be whitelisted:
          </Typography>
          <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
            <Typography variant="body2">• menu.thefoodo.com</Typography>
            <Typography variant="body2">• www.menu.thefoodo.com</Typography>
            <Typography variant="body2">• {websiteUrl.trim()}</Typography>
            <Typography variant="body2">• www.{websiteUrl.trim()}</Typography>
          </Box>
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={loading || !websiteUrl.trim() || !accountId}
        sx={{ mt: 2 }}
      >
        {loading ? "Whitelisting..." : "Whitelist All Domains"}
      </Button>

      {domainStatuses.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Whitelist Results:
          </Typography>
          {domainStatuses.map((result, index) => (
            <Alert
              key={index}
              severity={result.status === "success" ? "success" : "error"}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2">
                <strong>{result.domain}:</strong> {result.message}
              </Typography>
            </Alert>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WhiteListDomain;
